import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Skeleton } from '@machobear/design-system'

import { breakpoints, colors } from '@/styles'
import { isEmptyOrNull } from '@/utils'
import { useFilters } from '@/hooks/use-filters'
import useMediaQuery from '@/hooks/use-media-query'
import SortFilter from '@/features/Listings/listing-filter/sort-filter'
import { H4, Label } from '@/components/text'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  color: ${colors.regular};
  gap: 8px;
`

const ListingTitle = styled(H4)`
  margin: 0;
  word-break: break-word;
`

const CountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

type Props = {
  cityName?: string
  listingCount: string | number
  hideCount?: boolean
  hideHeader?: boolean
  isMapMode?: boolean
  CustomTitle?: React.ReactNode
  CustomCount?: React.ReactNode
  loading?: boolean
  loadingCount?: boolean
  showSort?: boolean
}

const Loading = ({
  displaySmaller,
  hideHeader,
  hideCount,
  showSort
}: {
  displaySmaller: boolean
  hideHeader?: boolean
  hideCount?: boolean
  showSort?: boolean
}) => {
  return displaySmaller ? (
    <>
      {!hideHeader && (
        <>
          <Skeleton.Shape width="80%" height={28} />
          <Skeleton.Shape width="100%" height={28} />
        </>
      )}
      <CountRow>
        {!hideCount && <Skeleton.Shape width={125} height={16} />}
        {showSort && <Skeleton.Shape width={68} height={16} />}
      </CountRow>
    </>
  ) : (
    <>
      {!hideHeader && <Skeleton.Shape width={600} height={32} />}
      <CountRow>
        {!hideCount && <Skeleton.Shape width={125} height={16} />}
        {showSort && <Skeleton.Shape width={68} height={16} />}
      </CountRow>
    </>
  )
}

const SearchTitle: React.FC<Props> = ({
  cityName,
  listingCount = 0,
  hideCount,
  hideHeader,
  isMapMode,
  CustomTitle,
  CustomCount,
  loading,
  loadingCount,
  showSort,
  ...rest
}) => {
  const { t } = useTranslation()
  const [filters] = useFilters()
  const displaySmaller = useMediaQuery(
    `(max-width: ${isMapMode ? breakpoints.map : breakpoints.phoneMax})`
  )

  const fallbackName = isEmptyOrNull(filters?.cities)
    ? t('b.listings.view.canada.text', 'Canada')
    : filters?.cities
  const cityNameText = cityName || fallbackName

  const getTitle = () => {
    if (CustomTitle) {
      return CustomTitle
    }
    return (
      <Trans
        i18nKey="b.listings.view.apartments_houses_condos_for_rent_city.title"
        values={{ cityName: cityNameText }}
        defaults="{{ cityName }} Apartments, Condos and Houses for Rent"
      />
    )
  }

  const getCount = () => {
    if (CustomCount) {
      return CustomCount
    }
    return (
      <Trans
        i18nKey="b.listings.view.rentals_found_count.text"
        values={{ listingCount }}
        defaults="{{ listingCount }} Rentals Found"
      />
    )
  }

  return (
    <Wrapper>
      {loading ? (
        <Loading
          hideHeader={hideHeader}
          hideCount={hideCount}
          displaySmaller={displaySmaller}
          showSort={showSort}
        />
      ) : (
        <>
          {!hideHeader && (
            <ListingTitle as="h1" {...rest}>
              {getTitle()}
            </ListingTitle>
          )}
          <CountRow>
            {!hideCount && (
              <>
                {loadingCount ? (
                  <Skeleton.Shape width={125} height={16} />
                ) : (
                  <Label fontSize="14px">{getCount()}</Label>
                )}
              </>
            )}
            {showSort && <SortFilter />}
          </CountRow>
        </>
      )}
    </Wrapper>
  )
}

export default SearchTitle
